import { Component, EventEmitter, Input, OnInit } from '@angular/core';
import { LoggerService } from '@services/logger.service';
import { Registry } from '@/registry.service';
import { Router } from '@angular/router';
import { UserService } from '@services/admin/user.service';
import { ToastrService } from 'ngx-toastr';
import { OptionSelect } from '@/models/option-select.model';
import { DynamicFormPopUpInterface } from '@components/dynamic-form/dynamic-form-pop-up-interface';
import { DynamicFormComponent } from '@components/dynamic-form/dynamic-form.component';
import { DynamicFormSubmitInterface } from '@components/dynamic-form/dynamic-form-submit-interface';
import { RentalMovementModel } from '@/models/rentals/rental-movement-model';
import { RentalMovementService } from '@services/data-source-tables/rentals/rental-movement.service';
import { RentalMovementFormSetting } from '@/form-settings/rentals/rental-movement-form-setting';
import { Subject } from 'rxjs';
import { DialogSelectClientRentComponent } from '@pages/rental-management/rent-practice-detail/rental-movements-list/dialog-form-rental-movement/dialog-select-client-rent/dialog-select-client-rent.component';
import { MatDialog } from '@angular/material/dialog';
import { RentalClientModel } from '@/models/rentals/rental-client-model';
import { RentalClientsService } from '@services/data-source-tables/rentals/rental-clients.service';
import { DialogSelectCompanyRentComponent } from '@pages/rental-management/rent-practice-detail/rental-movements-list/dialog-form-rental-movement/dialog-select-company-rent/dialog-select-company-rent.component';

@Component({
  selector: 'app-rental-movement-form',
  templateUrl: '../../../../../components/dynamic-form/dynamic-form.component.html',
  styleUrls: ['../../../../../components/dynamic-form/dynamic-form.component.scss'],
})
export class RentalMovementFormComponent
  extends DynamicFormComponent
  implements DynamicFormPopUpInterface, DynamicFormSubmitInterface, OnInit
{
  @Input() practiceId: number;
  @Input() data: RentalMovementModel;
  @Input() refreshRentalPracticeEvent$: Subject<any>;
  @Input() closeDialogRentalMovementEvent$: Subject<any>;
  @Input() closeButton: string;
  protected closeSelectClientEvent$: EventEmitter<RentalClientModel> =
    new EventEmitter<RentalClientModel>();
  protected brands: OptionSelect[] = [];
  protected closeSelectCompanyEvent$: EventEmitter<RentalClientModel> =
    new EventEmitter<RentalClientModel>();
  private isNew: boolean = true;
  private stateIsTerminated: boolean = false;

  constructor(
    protected toastr: ToastrService,
    protected router: Router,
    protected userService: UserService,
    protected rentalMovementService: RentalMovementService,
    protected dialogRef: MatDialog,
    protected rentalClientsService: RentalClientsService,
  ) {
    super(toastr, userService);
    this.primaryButton = `Salva noleggio`;
    this.secondaryBottomButton = this.closeButton ?? null;
    this.thirdButton = `Seleziona utilizzatore`;
    this.secondButton = `Seleziona società`;
    this.setupMeta();

    Registry.getInstance().set('RentalMovementFormComponent', this);
  }

  ngOnInit() {
    this.isNew = this.data.id === undefined;
    this.stateIsTerminated = this.data.stato === 'TERMINATO';

    this.refreshForm();

    // Precompila il campo kmConsegna per un nuovo noleggio
    if (this.isNew && this.practiceId) {
      this.prefillKmConsegnaFromLatestRental();
    }
  }

  setupMeta() {
    this.meta = RentalMovementFormSetting.setPracticeIsClosed(this.stateIsTerminated)
      .setIsNew(this.isNew)
      .getMeta()
      .sort((a: any, b: any) => a.order - b.order);
  }

  onSubmit() {
    this.submitButtonLoading = true;

    let rentalMovementModel = <RentalMovementModel>this.form.getRawValue();

    // Adjust dates to maintain the correct timezone
    if (rentalMovementModel.dataConsegna && rentalMovementModel.dataConsegna instanceof Date) {
      rentalMovementModel.dataConsegna = new Date(
        rentalMovementModel.dataConsegna.getTime() -
          rentalMovementModel.dataConsegna.getTimezoneOffset() * 60000,
      );
    }

    // Adjust dates to maintain the correct timezone
    if (
      rentalMovementModel.dataRientroPresunta &&
      rentalMovementModel.dataRientroPresunta instanceof Date
    ) {
      rentalMovementModel.dataRientroPresunta = new Date(
        rentalMovementModel.dataRientroPresunta.getTime() -
          rentalMovementModel.dataRientroPresunta.getTimezoneOffset() * 60000,
      );
    }

    if (
      rentalMovementModel.dataInizioLavorazioneVeicoloCliente &&
      rentalMovementModel.dataInizioLavorazioneVeicoloCliente instanceof Date
    ) {
      rentalMovementModel.dataInizioLavorazioneVeicoloCliente = new Date(
        rentalMovementModel.dataInizioLavorazioneVeicoloCliente.getTime() -
          rentalMovementModel.dataInizioLavorazioneVeicoloCliente.getTimezoneOffset() * 60000,
      );
    }

    console.log(rentalMovementModel);

    this.rentalMovementService
      .updateRentalMovement(this.practiceId, rentalMovementModel)
      .subscribe({
        next: (response) => {
          this.toastr.success('Noleggio salvato correttamente!');

          this.refreshRentalPracticeEvent$.next();
          this.bottomButtonAction();
        },
        error: (error) => {
          LoggerService.error('updateRentalMovement failed!');
          LoggerService.error(error);

          if (error.error.key === 'CONFLICT') {
            this.toastr.error(
              `Conflitto sulle date: esiste già una prenotazione in corso che si sovrappone al periodo del noleggio. Cancella prima la prenotazione, poi salva il noleggio.`,
            );
          } else {
            this.toastr.error(`Errore durante il salvataggio del noleggio.`);
          }

          setTimeout(() => {
            Registry.getInstance().set('saveInProgress', false);
            this.submitButtonLoading = false;
          }, 1000);
        },
        complete: () => {
          LoggerService.log('Request done');

          setTimeout(() => {
            Registry.getInstance().set('saveInProgress', false);

            if (this.closeDialogRentalMovementEvent$) {
              this.closeDialogRentalMovementEvent$.next();
            }
            this.submitButtonLoading = false;
          }, 1000);
        },
      });
  }

  bottomButtonAction(): void {
    if (this.closeDialogRentalMovementEvent$) {
      this.closeDialogRentalMovementEvent$.next();
    }
  }

  protected thirdButtonAction() {
    Registry.getInstance().set('closeSelectClientEvent$', this.closeSelectClientEvent$);

    const dialogSelectClient = this.dialogRef.open(DialogSelectClientRentComponent, {
      data: {
        service: this.rentalClientsService,
      },
      width: 'auto',
      panelClass: 'custom-modalbox',
    });

    Registry.getInstance()
      .get('closeSelectClientEvent$')
      .subscribe((client) => {
        this.form.get('bpUtilizzatore').setValue(client.numeroBp);
        this.form.get('denominazioneUtilizzatore').setValue(client.denominazione);
        this.form.get('codiceFiscaleUtilizzatore').setValue(client.codiceFiscale);
        this.form.get('emailUtilizzatore').setValue(client.email);
        this.form.get('patenteUtilizzatore').setValue(client.numeroPatente);
        this.form.get('telefonoUtilizzatore').setValue(client.numeroTelefono);
        this.form.get('cittaUtilizzatore').setValue(client.localita);
        this.form.get('indirizzoUtilizzatore').setValue(client.indirizzo);
        this.form.get('dataNascitaUtilizzatore').setValue(client.dataNascita);
        this.form.get('luogoNascitaUtilizzatore').setValue(client.luogoNascita);
        this.form
          .get('consensoIndigSoddClientelaUtilizzatore')
          .setValue(client.consensoIndigSoddClientela);
        this.form.get('consensoMarketingUtilizzatore').setValue(client.consensoMarketing);
        this.form
          .get('consensoMarketingTerzePartiUtilizzatore')
          .setValue(client.consensoMarketingTerzeParti);
        this.form.get('consensoProfilazioneUtilizzatore').setValue(client.consensoProfilazione);
        this.form.get('consensoAssicurazioneUtilizzatore').setValue(client.consensoAssicurazione);

        dialogSelectClient.close();
      });
  }

  protected secondButtonAction() {
    Registry.getInstance().set('closeSelectCompanyEvent$', this.closeSelectCompanyEvent$);

    const dialogSelectCompany = this.dialogRef.open(DialogSelectCompanyRentComponent, {
      data: {
        service: this.rentalClientsService,
      },
      width: 'auto',
      panelClass: 'custom-modalbox',
    });

    Registry.getInstance()
      .get('closeSelectCompanyEvent$')
      .subscribe((client) => {
        this.form.get('bpSocieta').setValue(client.numeroBp);
        this.form.get('societa').setValue(client.denominazione);
        this.form.get('pivaSocieta').setValue(client.piva);
        this.form.get('codiceFiscaleSocieta').setValue(client.codiceFiscale);
        this.form.get('cittaSocieta').setValue(client.localita);
        this.form.get('emailSocieta').setValue(client.email);
        this.form.get('telefonoSocieta').setValue(client.numeroTelefono);
        this.form.get('indirizzoSocieta').setValue(client.indirizzo);
        this.form
          .get('consensoIndigSoddClientelaSocieta')
          .setValue(client.consensoIndigSoddClientela);
        this.form.get('consensoMarketingSocieta').setValue(client.consensoMarketing);
        this.form
          .get('consensoMarketingTerzePartiSocieta')
          .setValue(client.consensoMarketingTerzeParti);
        this.form.get('consensoProfilazioneSocieta').setValue(client.consensoProfilazione);
        this.form.get('consensoAssicurazioneSocieta').setValue(client.consensoAssicurazione);
        dialogSelectCompany.close();
      });
  }

  private prefillKmConsegnaFromLatestRental() {
    this.rentalMovementService.getLatestRental(this.practiceId).subscribe({
      next: (latestRental) => {
        if (
          latestRental &&
          latestRental.kmRientro !== null &&
          latestRental.kmRientro !== undefined
        ) {
          // Precompila il campo kmConsegna con i km di rientro dell'ultimo noleggio
          this.form.get('kmConsegna')?.setValue(latestRental.kmRientro);
          LoggerService.log(
            `Precompiled kmConsegna with ${latestRental.kmRientro} from latest rental`,
            'prefillKmConsegnaFromLatestRental',
          );
        }
      },
      error: (error) => {
        LoggerService.error('Failed to prefill kmConsegna from latest rental:', error);
        // Errore silenzioso: il campo rimane vuoto, non mostriamo toast all'utente
      },
    });
  }
}
